import { Container, Reveal, SectionHead, BtnLink, PageHero, CheckList } from "../components/site/Primitives";
import { IMG, CLOSURES, PREFORMS } from "../data/site";
import { DownloadsSection } from "../components/site/Downloads";

const Swatches = ({ colours, id }) => (
  <div data-testid={`swatches-${id}`}>
    <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Colour range <span className="ml-2 text-navy">{colours.length}</span></p>
    <ul className="mt-4 flex flex-wrap gap-2.5">
      {colours.map((c) => (
        <li key={c.img} title={c.name || c.hex} className="h-7 w-7 rounded-full ring-1 ring-black/10 ring-offset-2 transition-transform duration-200 hover:scale-110" style={{ background: c.hex }} />
      ))}
    </ul>
  </div>
);

const ClosureRow = ({ c, i }) => (
  <Reveal>
    <article data-testid={`product-card-${c.id}`} className={`grid gap-10 lg:gap-16 lg:grid-cols-2 items-center ${i % 2 ? "lg:[&>*:first-child]:order-2" : ""}`}>
      <div className="img-zoom overflow-hidden aspect-[3/2] bg-mist"><img src={IMG(c.img)} alt={`${c.name} shown across the available colour range`} className="h-full w-full object-cover" /></div>
      <div>
        <div className="flex flex-wrap items-center gap-3">
          <h3 className="font-display text-2xl sm:text-3xl font-semibold text-ink tracking-tight">{c.name}</h3>
          <span className="border border-brand/30 bg-brand-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-brand-600">{c.tag}</span>
        </div>
        <p className="mt-4 text-slate-600 leading-relaxed">{c.blurb}</p>
        <div className="mt-6"><CheckList items={c.features} /></div>
        <div className="mt-8 border-t border-slate-200 pt-6"><Swatches colours={c.colours} id={c.id} /></div>
        <div className="mt-8 flex flex-wrap gap-3"><BtnLink to={`/products/${c.id}`} testId={`view-details-${c.id}`}>View details & specs</BtnLink><BtnLink to="/contact" variant="outline" testId={`request-details-${c.id}`}>Request a quote</BtnLink></div>
      </div>
    </article>
  </Reveal>
);

const PreformRow = ({ p }) => (
  <Reveal>
    <article data-testid={`product-card-${p.id}`} className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_1.1fr] items-start [&>*]:min-w-0 rounded-3xl bg-white border border-slate-200 p-6 sm:p-8 lg:p-10">
      <div>
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <h3 data-testid={`preform-heading-${p.id}`} className="font-display text-2xl sm:text-3xl font-semibold text-ink tracking-tight">{p.name}</h3>
          <span className="border border-brand/30 bg-brand-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-brand-600">{p.rows.length} grammages</span>
        </div>
        <div className="img-zoom overflow-hidden aspect-[3/2] bg-mist"><img src={IMG(p.img)} alt={p.name} className="h-full w-full object-cover" /></div>
        <p className="mt-6 text-slate-600 leading-relaxed">{p.blurb}</p>
        <div className="mt-6 flex flex-wrap gap-3"><BtnLink to={`/products/${p.id}`} testId={`view-details-${p.id}`}>View details & specs</BtnLink><BtnLink to="/contact" variant="outline" testId={`request-details-${p.id}`}>Request a quote</BtnLink></div>
      </div>
      <div className="overflow-x-auto border border-slate-200" data-testid={`preform-spec-table-${p.id}`}>
        <table className="w-full text-sm">
          <thead className="bg-brand-50 text-navy text-left">
            <tr>{["Application", "Grammage", "Bag weight"].map((h) => <th key={h} className="px-5 py-4 font-semibold text-xs uppercase tracking-wider">{h}</th>)}</tr>
          </thead>
          <tbody>
            {p.rows.map(([, a, g], k) => (
              <tr key={g} className={`border-t border-slate-100 transition-colors hover:bg-brand-50/60 ${k && a !== p.rows[k - 1][1] ? "border-t-2 border-t-slate-200" : ""}`}>
                <td className="px-5 py-3.5 text-slate-600">{a}</td>
                <td className="px-5 py-3.5 font-display font-semibold text-navy">{g}</td>
                <td className="px-5 py-3.5 text-slate-600">{p.bag}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </article>
  </Reveal>
);

const PreformSection = () => (
  <section className="bg-mist py-24 lg:py-32" id="preforms">
    <Container>
      <SectionHead eyebrow="Preforms" title="PET preforms" lead="Two neck finishes — Alaska 27mm and 26/22 short neck — in fifteen grammages, supplied in 20 kg (Alaska) and 25 kg (26/22) bags and engineered for 500 ml and 1000 ml applications." />
      <div className="mt-14 space-y-8">
        {PREFORMS.map((p) => <PreformRow key={p.id} p={p} />)}
      </div>
    </Container>
  </section>
);

export default function Products() {
  return (
    <div data-testid="products-page">
      <PageHero testId="products-hero" eyebrow="Product catalogue" title="Closures & preforms, engineered for your line" img={IMG("photos_caps_alaska_DSC09606")}
        lead="Three closure ranges across 43 moulded colours, and PET preforms in two neck finishes and fifteen grammages — built for consistent performance on high-speed filling lines." />
      <div className="bg-white pb-6"><img src={IMG("photos_hero_closure-range")} alt="The closure range shown in every produced colour" className="w-full" /></div>
      <section className="py-24 lg:py-32">
        <Container>
          <SectionHead eyebrow="Closures" title="Tamper-evident closures for water & beverage" lead="Technical datasheets, samples, and line trials can be arranged through our sales team — call, email or WhatsApp us." />
          <div className="mt-16 space-y-24">
            {CLOSURES.map((c, i) => <ClosureRow key={c.id} c={c} i={i} />)}
          </div>
        </Container>
      </section>
      <PreformSection />
      <DownloadsSection className="" />
    </div>
  );
}
